import { colors } from "shared/theme/color";
import { ClassCategory, PayrollRunStatus, ScheduleTimeStatus } from "shared/types/enum";
import {
  capitalizeFirstLetter,
  PAYROLL_RUN_STATUS_LABELS,
  PAYROLL_RUN_STATUS_TONE,
  SCHEDULE_DELETED_LABEL,
  SCHEDULE_DELETED_TONE,
  SCHEDULE_TIME_STATUS_LABELS,
  SCHEDULE_TIME_STATUS_TONE,
  StatusTone,
} from "shared/utils";
import { now } from "shared/utils/dayjs";
import { textPx } from "shared/constants/zoom";

// The six badge tones are defined with the label maps in shared/utils (see
// the "Status badges vs label chips" rule there); re-exported here so every
// existing importer keeps working.
export type { StatusTone } from "shared/utils";

// Design-system tones: {dot, bg, text}. The leading dot inherits the text
// colour. Every badge and chip in the admin renders through StatusBadge so
// badge styling lives in exactly one place.
const TONE: Record<StatusTone, { bg: string; text: string }> = {
  done: { bg: colors.successTint, text: colors.successDark },
  progress: { bg: colors.amberTint, text: colors.amberDark },
  navy: { bg: colors.navyTint, text: colors.navyDark },
  neutral: { bg: colors.bgSoft, text: colors.ink2 },
  error: { bg: colors.errorTint, text: colors.error },
  purple: { bg: colors.purpleTint, text: colors.purple },
};

// `dot` marks a STATUS (a state that changes over time). Label chips that say
// what a thing is (a role, a class type, Membership, Shared, Reward, a count)
// pass dot={false}; everything else about the chip stays identical.
export const StatusBadge = ({
  tone,
  label,
  dot = true,
}: {
  tone: StatusTone;
  label: string;
  dot?: boolean;
}) => {
  const { bg, text } = TONE[tone];
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 5,
        padding: "3px 9px",
        borderRadius: 6,
        fontSize: textPx(11),
        fontWeight: 600,
        lineHeight: `${textPx(16)}px`,
        whiteSpace: "nowrap",
        background: bg,
        color: text,
      }}
    >
      {dot && (
        <span
          style={{
            width: 6,
            height: 6,
            borderRadius: 999,
            background: "currentColor",
          }}
        />
      )}
      {label}
    </span>
  );
};

// Minimal shape needed to derive a schedule's status — satisfied by both the
// calendar's CalendarSchedule and the detail page's Schedule, so the badge is
// the single source for the status tag on Daftar Jadwal and Detail Jadwal.
export type ScheduleStatusInput = {
  is_cancelled: boolean;
  start_time: string | Date;
  end_time: string | Date;
};

// Maps a schedule to its tone + label using the app's time-based status model
// (labels and tones from shared/utils). `is_cancelled` is the V2 delete
// tombstone, so it reads "Deleted".
const statusOf = (
  schedule: ScheduleStatusInput
): { tone: StatusTone; label: string } => {
  if (schedule.is_cancelled)
    return { tone: SCHEDULE_DELETED_TONE, label: SCHEDULE_DELETED_LABEL };

  const current = now();
  const status = current.isAfter(schedule.end_time)
    ? ScheduleTimeStatus.Finished
    : current.isAfter(schedule.start_time)
      ? ScheduleTimeStatus.Ongoing
      : ScheduleTimeStatus.Upcoming;
  return {
    tone: SCHEDULE_TIME_STATUS_TONE[status],
    label: SCHEDULE_TIME_STATUS_LABELS[status],
  };
};

export const ScheduleStatusBadge = ({
  schedule,
}: {
  schedule: ScheduleStatusInput;
}) => {
  const { tone, label } = statusOf(schedule);
  return <StatusBadge tone={tone} label={label} />;
};

// Payroll run status badge: Draft (navy, being worked on) / Reopened (amber,
// an audited unlock of a previously finalized run) / Finalized (green,
// locked).
export const PayrollRunStatusBadge = ({ status }: { status: PayrollRunStatus }) => (
  <StatusBadge tone={PAYROLL_RUN_STATUS_TONE[status]} label={PAYROLL_RUN_STATUS_LABELS[status]} />
);

// Class-type chip for the merged list: Open (navy tint) / Private (soft
// grey). A label, not a status, so no dot.
export const ClassTypeBadge = ({ type }: { type?: ClassCategory }) => {
  if (!type) return null;
  return (
    <StatusBadge
      tone={type === ClassCategory.Open ? "navy" : "neutral"}
      label={capitalizeFirstLetter(type)}
      dot={false}
    />
  );
};
